import React, { useCallback, useState, useMemo, useRef, useEffect, type FocusEvent } from 'react';
import debounce from 'lodash/debounce';
import Select, { type FormatOptionLabelMeta } from '@atlaskit/select';
import { performGetRequest } from '@atlassian/jira-fetch';
import { useIntl } from '@atlassian/jira-intl';
import {
	filterOptionByLabelAndFilterValues,
	defaultSelectStyles,
} from '@atlassian/jira-issue-field-select-base';

import type { EpicOption, Group } from '../../common/types';
import { isEpicLinkFieldRenamedToParent } from '../../feature-flags';
import Menu from './menu';
import messages from './messages';
import CustomOption from './option';
import type { Props, RestAPIResponse } from './types';
import { transformSuggestions } from './utils';

export const FETCH_DEBOUNCE = 300;

export const EpicLinkEdit = (props: Props) => {
	const {
		onFocus,
		onBlur,
		value,
		onChange,
		autoCompleteUrl,
		projectKey,
		isInvalid,
		autoFocus = false,
		onCloseMenuOnScroll,
		isDropdownMenuFixedAndLayered,
		isDisabled = false,
		classNamePrefix,
		...fieldProps
	} = props;

	const { formatMessage } = useIntl();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [suggestions, setSuggestions] = useState<Group | undefined>();
	const [defaultSuggestions, setDefaultSuggestions] = useState<Group | undefined>();
	const [hasLastFetchFailed, setHasLastFetchFailed] = useState<boolean>(false);
	const [excludeDone, setExcludeDone] = useState<boolean>(true);

	const isInitialMount = useRef<boolean>(true);
	const lastQuery = useRef<string>('');

	const onCheckboxClick = useCallback(() => {
		setExcludeDone(!excludeDone);
	}, [excludeDone]);

	const componentProps = useMemo(
		() => ({ isChecked: !excludeDone, onChange: onCheckboxClick }),
		[excludeDone, onCheckboxClick],
	);

	const formatOptionLabel = useCallback(
		(option: EpicOption, { context }: FormatOptionLabelMeta<EpicOption>) => {
			if (context === 'menu') {
				return <CustomOption option={option} />;
			}

			return option.label;
		},
		[],
	);

	/** Fetching and transforming results logic below * */
	const handleSuggestions = useCallback(
		(results: RestAPIResponse, query: string) => {
			if (lastQuery.current === query) {
				const response = transformSuggestions(results, formatMessage);
				setSuggestions(response);
				if (query === '') {
					setDefaultSuggestions(response);
				}
			}
		},
		[formatMessage],
	);

	const requestSuggestions = useCallback(
		async (query = ''): Promise<void> => {
			try {
				const response = await performGetRequest(
					`${autoCompleteUrl}?excludeDone=${String(
						excludeDone,
					)}&projectKey=${projectKey}&maxResults=20&query=${query}`,
				);
				setIsLoading(false);
				handleSuggestions(response, query);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				setIsLoading(false);
				setHasLastFetchFailed(true);
				setSuggestions([]);
			}
		},
		[autoCompleteUrl, excludeDone, handleSuggestions, projectKey],
	);

	// go/jfe-eslint
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedRequestSuggestions = useCallback(debounce(requestSuggestions, FETCH_DEBOUNCE), [
		excludeDone,
	]);

	const onQueryChange = useCallback(
		(query: string): void => {
			debouncedRequestSuggestions.cancel();
			setHasLastFetchFailed(false);
			if (query) {
				lastQuery.current = query;
				setIsLoading(true);
				debouncedRequestSuggestions(query);
			} else {
				lastQuery.current = '';
				setIsLoading(false);
				setSuggestions(defaultSuggestions);
			}
		},
		[debouncedRequestSuggestions, defaultSuggestions],
	);

	const handleFocus = useCallback(
		(e: FocusEvent<HTMLElement>): void => {
			if (lastQuery.current === '' && !defaultSuggestions) {
				setIsLoading(true);
				requestSuggestions();
			}
			onFocus && onFocus(e);
		},
		[defaultSuggestions, onFocus, requestSuggestions],
	);

	const filterOption = useCallback(
		(option: EpicOption): boolean => {
			if (hasLastFetchFailed) {
				// If the fetch has failed, we will show a disabled option to indicate the error to users
				return true;
			}
			return filterOptionByLabelAndFilterValues<EpicOption>(option, lastQuery.current || '');
		},
		[hasLastFetchFailed],
	);

	const getNoOptionsMessage = useCallback(
		(): string =>
			hasLastFetchFailed
				? formatMessage(messages.failedFetch)
				: formatMessage(messages.noOptionsMessage),
		[formatMessage, hasLastFetchFailed],
	);
	const getLoadingMessage = useCallback(
		(): string => formatMessage(messages.loadingMessage),
		[formatMessage],
	);

	useEffect(() => {
		if (!isInitialMount.current) {
			setIsLoading(true);
			requestSuggestions(lastQuery.current);
		}
		isInitialMount.current = false;
	}, [excludeDone, requestSuggestions]);

	const placeholder = isEpicLinkFieldRenamedToParent()
		? formatMessage(messages.placeholderRenamedParent)
		: formatMessage(messages.placeholder);

	return (
		<Select<EpicOption>
			{...fieldProps}
			classNamePrefix={classNamePrefix}
			options={suggestions}
			value={value}
			formatOptionLabel={formatOptionLabel}
			onBlur={onBlur}
			onFocus={handleFocus}
			// @ts-expect-error - TS2322 - Type '(arg1: EpicOption) => void' is not assignable to type '(value: EpicOption | null, action: ActionMeta<EpicOption>) => void'.
			onChange={onChange}
			menuPosition={isDropdownMenuFixedAndLayered === true ? 'fixed' : undefined}
			styles={defaultSelectStyles}
			closeMenuOnScroll={onCloseMenuOnScroll}
			isMulti={false}
			isLoading={isLoading}
			onInputChange={onQueryChange}
			loadingMessage={getLoadingMessage}
			noOptionsMessage={getNoOptionsMessage}
			placeholder={placeholder}
			// @ts-expect-error - TS2322 - Type '(props: Props) => JSX.Element' is not assignable to type 'ComponentType<MenuProps<EpicOption, false, GroupTypeBase<EpicOption>>> | undefined'.
			components={{ Menu }}
			componentsProps={componentProps}
			autoFocus={autoFocus}
			filterOption={filterOption}
			// @ts-expect-error - TS2322 - Type '"error" | null' is not assignable to type 'ValidationState | undefined'.
			validationState={isInvalid === true ? 'error' : null}
			hideSelectedOptions
			isClearable
			isDisabled={isDisabled}
		/>
	);
};

export default EpicLinkEdit;
